import {
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit3,
  FileSearch,
  Loader2,
  Printer,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const FORM_NAME = "SBIAOF-S3";
const PAGE_SIZE = 25;
const LIST_ENDPOINT = "/backend/api/forms.php";
const EDIT_ROUTE = "/ModifyMyForm/SBIAOF3/Single";
const PRINT_URL = "/ModifyMyForm/SBIAOF3/PrintFormSingle.php";

export default function FormsListSingle() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [forms, setForms] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: PAGE_SIZE,
    totalPages: 1,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(true);
  const [printingRefno, setPrintingRefno] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => {
    return () => {
      if (pdfUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);

    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const loadForms = useCallback(async () => {
    setLoading(true);

    try {
      const requestBody = new URLSearchParams({
        form_name: FORM_NAME,
        page: String(page),
        q: debouncedSearch,
      });

      const response = await fetch(LIST_ENDPOINT, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type":
            "application/x-www-form-urlencoded; charset=UTF-8",
          Accept: "application/json",
        },
        body: requestBody.toString(),
      });

      const responseText = await response.text();
      let result;

      try {
        result = responseText
          ? JSON.parse(responseText)
          : {};
      } catch {
        throw new Error(
          responseText.trim()
            ? `Invalid server response: ${responseText.substring(0, 200)}`
            : `forms.php returned an empty response. HTTP ${response.status}.`
        );
      }

      if (response.status === 401) {
        toast.error(
          result.message || "Your login session has expired."
        );
        navigate("/login", { replace: true });
        return;
      }

      if (!response.ok || !result.success) {
        throw new Error(
          result.message ||
            `Unable to load saved forms. HTTP ${response.status}.`
        );
      }

      setForms(
        Array.isArray(result.forms)
          ? result.forms
          : []
      );

      setPagination(
        result.pagination || {
          page,
          perPage: PAGE_SIZE,
          totalPages: 1,
          totalRecords: 0,
        }
      );
    } catch (error) {
      setForms([]);
      setPagination({
        page: 1,
        perPage: PAGE_SIZE,
        totalPages: 1,
        totalRecords: 0,
      });
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to load saved forms."
      );
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, navigate]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const handleModify = (refno) => {
    if (!refno) {
      toast.error("Reference number is unavailable.");
      return;
    }

    navigate(EDIT_ROUTE, {
      state: {
        form_name: FORM_NAME,
        refno,
      },
    });
  };

  const handlePrint = async (refno) => {
    if (!refno) {
      toast.error("Reference number is unavailable.");
      return;
    }

    if (printingRefno) {
      return;
    }

    setPrintingRefno(refno);

    try {
      const postData = new FormData();
      postData.append("refno", refno);

      const response = await fetch(PRINT_URL, {
        method: "POST",
        body: postData,
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/pdf, application/json",
        },
      });

      if (!response.ok) {
        const responseText = await response.text();
        let message = "Unable to generate the PDF.";

        try {
          const result = responseText
            ? JSON.parse(responseText)
            : {};
          message = result.message || message;
        } catch {
          if (responseText.trim()) {
            message = responseText.substring(0, 200);
          }
        }

        throw new Error(message);
      }

      const contentType =
        response.headers.get("content-type") || "";

      if (!contentType.includes("application/pdf")) {
        const responseText = await response.text();
        throw new Error(
          responseText ||
            "The server did not return a PDF file."
        );
      }

      const pdfBlob = await response.blob();
      const objectUrl = URL.createObjectURL(pdfBlob);

      setPdfUrl((previousUrl) => {
        if (previousUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(previousUrl);
        }
        return objectUrl;
      });

      setShowPdf(true);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to generate the PDF."
      );
    } finally {
      setPrintingRefno("");
    }
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI Single Account Form"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
      <div className="pointer-events-none absolute inset-0 flex justify-center">
        <div className="mt-8 h-[540px] w-[540px] rounded-full bg-gradient-to-tr from-sky-400/15 to-violet-500/20 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-6xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-foreground/5 hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <section className="overflow-hidden rounded-3xl border border-border/70 bg-background/80 shadow-xl backdrop-blur-xl">
          <header className="border-b border-border/70 p-6 sm:p-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-lg">
                  <FileSearch className="h-7 w-7" />
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    SBI Account Opening Form
                  </p>

                  <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
                    Modify Single Forms
                  </h1>

                  <p className="mt-1.5 text-sm text-muted-foreground">
                    Search, modify or print your saved SBI
                    Single Account Opening Forms.
                  </p>
                </div>
              </div>

              <div className="relative w-full md:w-80">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                <input
                  type="search"
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search name or reference number"
                  className="h-11 w-full rounded-full border border-border bg-background/70 pl-11 pr-4 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
          </header>

          <div className="p-5 sm:p-7">
            <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>SBI Single Account Forms</span>
              <span>
                {pagination.totalRecords}{" "}
                {pagination.totalRecords === 1
                  ? "record"
                  : "records"}
              </span>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border/70">
              {loading ? (
                <LoadingState />
              ) : forms.length === 0 ? (
                <EmptyState
                  hasSearch={Boolean(debouncedSearch)}
                />
              ) : (
                <>
                  <DesktopTable
                    forms={forms}
                    printingRefno={printingRefno}
                    onModify={handleModify}
                    onPrint={handlePrint}
                  />
                  <MobileList
                    forms={forms}
                    printingRefno={printingRefno}
                    onModify={handleModify}
                    onPrint={handlePrint}
                  />
                </>
              )}
            </div>

            {pagination.totalPages > 1 && (
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                loading={loading}
                onPrevious={() =>
                  setPage((currentPage) =>
                    Math.max(1, currentPage - 1)
                  )
                }
                onNext={() =>
                  setPage((currentPage) =>
                    Math.min(
                      pagination.totalPages,
                      currentPage + 1
                    )
                  )
                }
              />
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function DesktopTable({
  forms,
  printingRefno,
  onModify,
  onPrint,
}) {
  return (
    <div className="hidden overflow-x-auto md:block">
      <table className="w-full min-w-[850px] table-fixed text-sm">
        <thead className="bg-foreground/[0.04] text-left text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          <tr>
            <th className="w-[9%] whitespace-nowrap px-5 py-3.5">
              S No.
            </th>
            <th className="w-[16%] whitespace-nowrap px-5 py-3.5">
              Date
            </th>
            <th className="w-[37.5%] whitespace-nowrap px-5 py-3.5">
              Customer Name
            </th>
            <th className="w-[14.5%] whitespace-nowrap px-5 py-3.5">
              Reference Number
            </th>
            <th className="w-[23%] whitespace-nowrap px-5 py-3.5 text-right">
              Actions
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-border/60">
          {forms.map((form, index) => {
            const isPrinting =
              printingRefno === form.refno;

            return (
              <tr
                key={form.refno || index}
                className="transition hover:bg-foreground/[0.025]"
              >
              <td className="px-5 py-4 text-muted-foreground">
                {form.serialNumber || index + 1}
              </td>
              <td className="whitespace-nowrap px-5 py-4">
                {form.date || "—"}
              </td>
              <td className="px-5 py-4 font-semibold">
                {form.customerName ||
                  form.entname ||
                  "—"}
              </td>
              <td className="px-5 py-4 font-mono text-xs">
                {form.refno || "—"}
              </td>
              <td className="px-5 py-4">
                <div className="flex justify-end gap-2">
                  <ActionButton
                    icon={Edit3}
                    label="Modify"
                    onClick={() =>
                      onModify(form.refno)
                    }
                  />
                  <ActionButton
                    icon={isPrinting ? Loader2 : Printer}
                    label={isPrinting ? "Opening..." : "Print"}
                    loading={isPrinting}
                    onClick={() =>
                      onPrint(form.refno)
                    }
                  />
                </div>
              </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MobileList({
  forms,
  printingRefno,
  onModify,
  onPrint,
}) {
  return (
    <div className="divide-y divide-border/60 md:hidden">
      {forms.map((form, index) => {
        const isPrinting =
          printingRefno === form.refno;

        return (
          <article
            key={form.refno || index}
            className="p-4"
          >
          <p className="font-semibold">
            {form.customerName ||
              form.entname ||
              "Customer name unavailable"}
          </p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">
            {form.refno || "Reference unavailable"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {form.date || "Date unavailable"}
          </p>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <ActionButton
              icon={Edit3}
              label="Modify"
              onClick={() => onModify(form.refno)}
            />
            <ActionButton
              icon={isPrinting ? Loader2 : Printer}
              label={isPrinting ? "Opening..." : "Print"}
              loading={isPrinting}
              onClick={() => onPrint(form.refno)}
            />
          </div>
          </article>
        );
      })}
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
  loading = false,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex h-9 items-center justify-center gap-1.5 rounded-full border border-border px-3.5 text-xs font-semibold text-muted-foreground transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Icon
        className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
      />
      {label}
    </button>
  );
}

function LoadingState() {
  return (
    <div className="flex min-h-64 items-center justify-center gap-3 text-sm text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
      Loading saved forms...
    </div>
  );
}

function EmptyState({ hasSearch }) {
  return (
    <div className="flex min-h-64 flex-col items-center justify-center px-4 text-center">
      <FileSearch className="h-10 w-10 text-muted-foreground/50" />
      <h3 className="mt-3 font-semibold">
        No forms found
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {hasSearch
          ? "No saved form matches your search."
          : "You have not saved any SBI Single forms yet."}
      </p>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  loading,
  onPrevious,
  onNext,
}) {
  return (
    <div className="mt-5 flex items-center justify-center gap-3">
      <button
        type="button"
        disabled={page <= 1 || loading}
        onClick={onPrevious}
        className="h-9 rounded-full border border-border px-4 text-xs font-semibold transition hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
      >
        Previous
      </button>

      <span className="text-xs text-muted-foreground">
        Page {page} of {totalPages}
      </span>

      <button
        type="button"
        disabled={page >= totalPages || loading}
        onClick={onNext}
        className="h-9 rounded-full border border-border px-4 text-xs font-semibold transition hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}

